import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Divider,
  Skeleton,
  TextField,
  CircularProgress,
  Chip,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  CalendarToday,
  History,
  ChevronLeft,
  ChevronRight,
  Email,
  Delete,
  Edit,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLimits } from '../hooks/useLimits';
import { useSafeAreas } from '../contexts/SafeAreasContext';
import { getUserAndInvitedEvents, createEvent, deleteEvent, Event } from '../services/eventsService';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { LimitReachedDialog } from '../components/subscription/LimitReachedDialog';
import { Dialog } from '../layout/Dialog';
import { EventsDialog } from '../components/eventsDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const EventsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { plan } = useSubscription();
  const { safeAreas, isReady: safeAreasReady } = useSafeAreas();
  const {
    canCreateEvent,
    eventsRemaining,
    isLoading: limitsLoading,
    checkEventLimit,
    refreshLimits
  } = useLimits();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [limitDialog, setLimitDialog] = useState({ open: false, message: '' });
  const [nextEventCountdown, setNextEventCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, eventId: '', eventTitle: '' });
  const [deleting, setDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setSelectedEvent(null);
      setShowEditDialog(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Filter events based on history toggle
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const displayedEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    return showHistory ? eventDate < now : eventDate >= now;
  }).sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    // Past events: newest first (desc), Upcoming: soonest first (asc)
    return showHistory ? dateB - dateA : dateA - dateB;
  });

  // Carregar eventos do usuário
  useEffect(() => {
    const loadEvents = async () => {
      if (!user?.uid) {
        setEventsLoading(false);
        return;
      }

      try {
        setEventsLoading(true);
        const userEvents = await getUserAndInvitedEvents(user.uid);
        setEvents(userEvents);
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    };

    loadEvents();
  }, [user?.uid]);

  // Calcular countdown para próximo evento
  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();

      // Filtrar apenas eventos futuros
      const upcomingEvents = events.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= now;
      });

      if (upcomingEvents.length === 0) {
        setNextEventCountdown({ days: 0, hours: 0, minutes: 0 });
        return;
      }

      // Ordenar por data e pegar o próximo evento
      const sortedEvents = upcomingEvents.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      const nextEvent = new Date(sortedEvents[0].date);

      // Adicionar o horário do evento
      const [hours, minutes] = sortedEvents[0].time.split(':').map(Number);
      nextEvent.setHours(hours, minutes, 0, 0);

      const diffTime = nextEvent.getTime() - now.getTime();
      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const hrs = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

      setNextEventCountdown({
        days: Math.max(0, days),
        hours: Math.max(0, hrs),
        minutes: Math.max(0, mins)
      });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, [events]);

  const monthNames = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  const handleCreateEvent = async () => {
    try {
      // Verificação assíncrona precisa de limite
      const limitCheck = await checkEventLimit();

      if (!limitCheck.allowed) {
        setLimitDialog({
          open: true,
          message: limitCheck.message || 'Limite de eventos atingido'
        });
        return;
      }

      setSelectedEvent(null);
      setShowEditDialog(true);
    } catch (error) {
      console.error('Erro ao verificar limite:', error);
      setLimitDialog({
        open: true,
        message: 'Erro ao verificar limite. Tente novamente.'
      });
    }
  };

  const handleSubmitEvent = async () => {
    if (!newEventTitle.trim() || !user?.uid) return;

    try {
      setCreating(true);

      // Verificar limite novamente antes de criar
      const limitCheck = await checkEventLimit();
      if (!limitCheck.allowed) {
        setLimitDialog({
          open: true,
          message: limitCheck.message || 'Limite de eventos atingido'
        });
        setShowCreateForm(false);
        return;
      }

      // Criar evento real no Firebase
      const eventData = {
        title: newEventTitle.trim(),
        description: 'Evento criado via Hostly',
        date: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)), // 7 dias no futuro
        time: '19:00',
        location: '',
        guestCount: 0,
        status: 'planned' as const,
      };

      await createEvent(user.uid, eventData);

      // Recarregar eventos
      const userEvents = await getUserAndInvitedEvents(user.uid);
      setEvents(userEvents);

      // Refresh limits
      await refreshLimits();

      // Limpar formulário
      setNewEventTitle('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      setLimitDialog({
        open: true,
        message: 'Erro ao criar evento. Tente novamente.'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, event: Event) => {
    e.stopPropagation();
    setDeleteDialog({
      open: true,
      eventId: event.id,
      eventTitle: event.title
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.eventId || !user?.uid) return;

    try {
      setDeleting(true);
      await deleteEvent(deleteDialog.eventId, user.uid);

      const userEvents = await getUserAndInvitedEvents(user.uid);
      setEvents(userEvents);
      await refreshLimits();

      setDeleteDialog({ open: false, eventId: '', eventTitle: '' });
    } catch (error) {
      console.error('Erro ao excluir evento:', error);
      setLimitDialog({
        open: true,
        message: 'Erro ao excluir evento. Tente novamente.'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent, event: Event) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowEditDialog(true);
  };

  const handleEventUpdated = async () => {
    if (!user?.uid) return;
    const userEvents = await getUserAndInvitedEvents(user.uid);
    setEvents(userEvents);
    setShowEditDialog(false);
    setSelectedEvent(null);
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const days = getDaysInMonth(currentMonth);

  // Loading state - incluir safe areas
  if (authLoading || limitsLoading || !safeAreasReady) {
    return (
      <>
        <TopBar
          title="Eventos"
          icon={<CalendarToday sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
        <Box sx={{
          paddingTop: `${70 + safeAreas.top}px`,
          paddingBottom: `${160 + safeAreas.bottom}px`,
          paddingLeft: `${16 + safeAreas.left}px`,
          paddingRight: `${16 + safeAreas.right}px`,
          maxWidth: '400px',
          margin: '0 auto',
        }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: theme.radii.medium, mb: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: theme.radii.medium }} />
        </Box>
      </>
    );
  }

  // Require authentication
  if (!user) {
    navigate('/login');
    return null;
  }

  // Estilos tipados
  const containerStyles: SxProps<Theme> = {
    paddingBottom: `${160 + safeAreas.bottom}px`,
    paddingTop: `${70 + safeAreas.top}px`,
    paddingLeft: `${16 + safeAreas.left}px`,
    paddingRight: `${16 + safeAreas.right}px`,
    maxWidth: { xs: '400px', md: '1200px' },
    width: '100%',
    margin: '0 auto',
    minHeight: '100vh',
  };

  const nextEventsCardStyles: SxProps<Theme> = {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.radii.medium,
    marginBottom: 1.5,
    border: 'none',
    boxShadow: 'none',
  };

  const actionButtonStyles: SxProps<Theme> = {
    backgroundColor: theme.colors.primary,
    color: theme.colors.text.contrast,
    borderRadius: theme.radii.medium,
    padding: '6px 12px',
    fontSize: '0.7rem',
    fontWeight: 'normal',
    textTransform: 'none',
    flex: 1,
    maxWidth: '180px',
    '&:hover': {
      backgroundColor: theme.colors.primary,
      opacity: 0.8,
    },
  };



  return (
    <>
      <TopBar
        title="Eventos"
        icon={<CalendarToday sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate('/dashboard')}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />
      <Box sx={containerStyles}>

        {/* Prompt de upgrade se próximo do limite */}
        {eventsRemaining <= 2 && eventsRemaining !== -1 && (
          <UpgradePrompt
            feature="eventos"
            currentPlan={plan}
            onUpgrade={handleUpgrade}
          />
        )}

        {/* Próximos Eventos */}
        <Card sx={nextEventsCardStyles}>
          <CardContent sx={{ padding: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                sx={{
                  color: theme.colors.text.primary,
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                }}
              >
                {showHistory ? 'HISTÓRICO DE EVENTOS' : 'PRÓXIMOS EVENTOS'}
              </Typography>
              {eventsRemaining !== -1 && !showHistory && (
                <Typography
                  sx={{
                    color: theme.colors.text.secondary,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  {eventsRemaining} restantes
                </Typography>
              )}
            </Box>

            {eventsLoading ? (
              <Box>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: theme.radii.small, mb: 1 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: theme.radii.small }} />
              </Box>
            ) : displayedEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  {showHistory ? 'Nenhum evento anterior' : 'Nenhum próximo evento'}
                </Typography>
                {!showHistory && (
                  <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', mt: 1 }}>
                    Crie seu primeiro evento!
                  </Typography>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {displayedEvents.map((event, index) => (
                  <Grid size={{ xs: 12, md: 6 }} key={index}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginBottom: 0.5,
                      p: 1,
                      borderRadius: theme.radii.medium,
                      border: { md: `1px solid ${theme.colors.border}` },
                      height: '100%',
                    }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{
                          backgroundColor: theme.colors.background,
                          borderRadius: theme.radii.medium,
                          width: 48,
                          height: 48,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.colors.text.primary,
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                        }}>
                          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1, fontWeight: 'bold' }}>
                            {new Date(event.date).getDate().toString().padStart(2, '0')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1, fontWeight: 'bold' }}>
                            {new Date(event.date).toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase()}
                          </Typography>
                        </Box>

                        <Box sx={{
                          backgroundColor: theme.colors.background,
                          borderRadius: theme.radii.medium,
                          width: 48,
                          height: 48,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.colors.text.primary,
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                        }}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1, fontWeight: 'bold' }}>
                            {new Date(event.date).toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase()}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1, fontWeight: 'bold' }}>
                            {event.time.substring(0, 2)}H
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            color: theme.colors.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {event.title.replace(' (Rascunho)', '')}
                          </Typography>
                          {event.title.includes('(Rascunho)') && (
                            <Chip
                              label="Rascunho"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 'bold',
                                backgroundColor: '#f59e0b',
                                color: '#fff',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                        <Button
                          onClick={() => navigate(`/events/${event.id}`)}
                          sx={{
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.contrast,
                            borderRadius: theme.radii.medium,
                            padding: '8px 12px',
                            fontSize: '0.75rem',
                            fontWeight: 'normal',
                            textTransform: 'none',
                            maxWidth: '180px',
                            lineHeight: 1.2,
                            '&:hover': {
                              backgroundColor: theme.colors.primary,
                              opacity: 0.8,
                            },
                          }}
                        >
                          VER TUDO SOBRE<br />ESTE EVENTO
                        </Button>
                      </Box>

                      {event.userId === user?.uid ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <IconButton
                            onClick={(e) => handleEditClick(e, event)}
                            size="small"
                            sx={{
                              color: theme.colors.text.secondary,
                              '&:hover': {
                                color: theme.colors.primary,
                                backgroundColor: `${theme.colors.primary}10`,
                              },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleDeleteClick(e, event)}
                            size="small"
                            sx={{
                              color: theme.colors.text.secondary,
                              '&:hover': {
                                color: '#d32f2f',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Chip label={(event.editorUserIds || []).includes(user?.uid || '') ? 'Colaborador' : 'Convidado'} size="small" sx={{ backgroundColor: `${theme.colors.primary}18`, color: theme.colors.primary, fontWeight: 600, height: 22 }} />
                      )}
                    </Box>
                    {index < displayedEvents.length - 1 && (
                      <Divider sx={{ borderColor: theme.colors.border, marginBottom: 2, display: { md: 'none' } }} />
                    )}
                  </Grid>
                ))}
              </Grid>)}

            <Typography
              sx={{
                textAlign: 'center',
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '0.75rem',
                marginTop: 1,
              }}
            >
              VER MAIS
            </Typography>
          </CardContent>
        </Card>

        {/* Contador para próximo evento */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          marginBottom: 1.5,
          flexWrap: 'wrap'
        }}>
          {events.length > 0 ? (
            nextEventCountdown.days > 0 ? (
              <>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: theme.colors.text.primary }}>
                  FALTAM
                </Typography>
                <Box sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  padding: '2px 8px',
                  borderRadius: theme.radii.small,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.05em',
                }}>
                  {nextEventCountdown.days} {nextEventCountdown.days === 1 ? 'DIA' : 'DIAS'}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: theme.colors.text.primary }}>
                  PARA O PRÓXIMO EVENTO
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: theme.colors.primary }}>
                O PRÓXIMO EVENTO É HOJE
              </Typography>
            )
          ) : (
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: theme.colors.text.secondary }}>
              Nenhum evento agendado
            </Typography>
          )}
        </Box>

        {/* Botões de ação */}
        <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, justifyContent: 'center' }}>
          <Button
            startIcon={<History />}
            onClick={() => setShowHistory(!showHistory)}
            sx={{
              ...actionButtonStyles,
              backgroundColor: showHistory ? theme.colors.secondary : theme.colors.primary,
            }}
          >
            {showHistory ? 'Ver Próximos' : 'Histórico'}
          </Button>

          <Button
            startIcon={<CalendarToday />}
            onClick={handleCreateEvent}
            disabled={!canCreateEvent}
            sx={{
              ...actionButtonStyles,
              opacity: !canCreateEvent ? 0.5 : 1,
              '&:disabled': {
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
              },
            }}
          >
            Novo evento
            {eventsRemaining > 0 && eventsRemaining !== -1 && (
              <Typography component="span" sx={{ ml: 0.5, fontSize: '0.6rem', opacity: 0.8 }}>
                ({eventsRemaining})
              </Typography>
            )}
          </Button>
        </Box>

        {/* Calendário */}
        <Box sx={{ marginBottom: 2, maxWidth: '400px', mx: 'auto' }}>
          {/* Header do calendário */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 1.5
          }}>
            <IconButton
              onClick={() => navigateMonth('prev')}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Typography sx={{
              fontSize: '0.75rem',
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              minWidth: '80px',
              textAlign: 'center'
            }}>
              {monthNames[currentMonth.getMonth()]}
            </Typography>

            <IconButton
              onClick={() => navigateMonth('next')}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Dias da semana */}
          <Grid container columns={7} spacing={0} sx={{ marginBottom: 1 }}>
            {weekDays.map((day, index) => (
              <Grid key={day} size={1}>
                <Typography sx={{
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: index === 0 ? theme.colors.secondary : theme.colors.text.primary,
                  padding: '4px 0'
                }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Dias do mês */}
          <Grid container columns={7} spacing={0}>
            {days.map((day, index) => (
              <Grid key={index} size={1}>
                <Box sx={{
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {day && (
                    <>
                      <Typography sx={{
                        fontSize: '0.75rem',
                        color: index % 7 === 0 ? theme.colors.secondary : theme.colors.text.primary,
                        textAlign: 'center'
                      }}>
                        {day}
                      </Typography>
                      {(() => {
                        const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                        const hasEvent = events.some(event => {
                          const eventDate = new Date(event.date);
                          return eventDate.getFullYear() === currentDate.getFullYear() &&
                            eventDate.getMonth() === currentDate.getMonth() &&
                            eventDate.getDate() === currentDate.getDate();
                        });
                        return hasEvent ? (
                          <Box sx={{
                            position: 'absolute',
                            border: `1px solid ${theme.colors.secondary}`,
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            pointerEvents: 'none'
                          }} />
                        ) : null;
                      })()}
                    </>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Botão de convite */}
        <Button
          startIcon={<Email />}
          fullWidth
          disabled={displayedEvents.length === 0}
          onClick={() => {
            if (displayedEvents.length > 0) {
              const nextEvent = displayedEvents[0];
              navigate(`/events/${nextEvent.id}`, { state: { openInvite: true } });
            }
          }}
          sx={{
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.onSecondary,
            borderRadius: theme.radii.medium,
            padding: '12px',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            textTransform: 'none',
            marginBottom: 3,
            maxWidth: '400px',
            display: 'flex',
            mx: 'auto',
            '&:hover': {
              backgroundColor: theme.colors.secondary,
              opacity: 0.8,
            },
            '&:disabled': {
              backgroundColor: theme.colors.border,
              color: theme.colors.text.secondary,
              opacity: 0.5,
            }
          }}
        >
          Enviar convite para evento
        </Button>



        {/* Bottom Navigation */}
        <BottomNavigation />

        {/* Dialog de limite atingido */}
        <LimitReachedDialog
          open={limitDialog.open}
          feature="eventos"
          onClose={() => setLimitDialog({ open: false, message: '' })}
          onUpgrade={handleUpgrade}
          currentPlan={plan}
        />

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, eventId: '', eventTitle: '' })}
          scroll="paper"
          title="Excluir Evento"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setDeleteDialog({ open: false, eventId: '', eventTitle: '' })}
                disabled={deleting}
                sx={{ color: theme.colors.text.secondary }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={deleting}
                variant="contained"
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}
              >
                {deleting ? <CircularProgress size={20} color="inherit" /> : 'Excluir'}
              </Button>
            </Box>
          }
        >
          <Typography sx={{ color: theme.colors.text.primary }}>
            Tem certeza que deseja excluir o evento <strong>{deleteDialog.eventTitle}</strong>?
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.colors.text.secondary }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </Dialog>

        {/* Dialog de Edição/Criação Completa */}
        {(showEditDialog) && (
          <EventsDialog
            open={showEditDialog}
            onClose={() => {
              setShowEditDialog(false);
              setSelectedEvent(null);
            }}
            eventData={selectedEvent ? {
              id: selectedEvent.id,
              title: selectedEvent.title,
              date: new Date(selectedEvent.date).toISOString().split('T')[0],
              time: selectedEvent.time,
              location: selectedEvent.location,
              description: selectedEvent.description,
              guests: selectedEvent.guests,
              recipes: selectedEvent.recipes,
              inventoryItems: selectedEvent.inventoryItems,
              tasks: selectedEvent.tasks,
              outfit: selectedEvent.outfit,
              guestCount: selectedEvent.guestCount,
            } : undefined}
            onEventCreated={handleEventUpdated}
          />
        )}

        {/* Dialog de criação de evento */}
        <Dialog
          open={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          scroll="paper"
          title="Criar Novo Evento"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setShowCreateForm(false)}
                sx={{ color: theme.colors.text.secondary }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmitEvent}
                disabled={!newEventTitle.trim() || creating}
                variant="contained"
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  '&:hover': {
                    backgroundColor: theme.colors.primary,
                    opacity: 0.8,
                  },
                }}
              >
                {creating ? <CircularProgress size={20} color="inherit" /> : 'Criar Evento'}
              </Button>
            </Box>
          }
        >
          <Box sx={{ py: 1 }}>
            <TextField
              fullWidth
              label="Nome do Evento"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
              variant="outlined"
              placeholder="Ex: Jantar de Aniversário"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.colors.border,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.colors.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.colors.primary,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.colors.text.secondary,
                  '&.Mui-focused': {
                    color: theme.colors.primary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: theme.colors.text.primary,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: theme.colors.text.secondary,
                fontSize: '0.75rem'
              }}
            >
              Você poderá adicionar mais detalhes depois de criar o evento.
              {eventsRemaining > 0 && eventsRemaining !== -1 && (
                <> Restam <strong>{eventsRemaining}</strong> eventos disponíveis neste mês.</>
              )}
            </Typography>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};